/*
 * Copyright (c) 2024, King Living
 * Site Configuration - Region-specific settings
 *
 * Contains default locations and settings for each site/region
 */

// Default search radius if not configured in site preferences (in km)
export const DEFAULT_STORE_SEARCH_RADIUS = 1000;

// Fallback search radius to ensure we get stores when none found in initial radius
export const FALLBACK_STORE_SEARCH_RADIUS = 10000;

// Default number of stores to display
export const DEFAULT_STORE_LIMIT = 20;

// Fallback store limit when expanding search
export const FALLBACK_STORE_LIMIT = 10;

/**
 * Site configuration map
 * Key: Site ID (e.g., 'kl-au')
 * Value: Site configuration object
 */
export const SITE_CONFIG = {
  'kl-au': {
    siteId: 'kl-au',
    country: 'Australia',
    countryCode: 'AU',
    defaultLocale: 'en-AU',
    currency: 'AUD',
    // Default location: Sydney (center of major population)
    defaultLocation: {
      lat: -33.8688,
      lng: 151.2093,
      name: 'Sydney',
    },
    distanceUnit: 'km',
    searchRadius: 1000, // km
  },
  'kl-nz': {
    siteId: 'kl-nz',
    country: 'New Zealand',
    countryCode: 'NZ',
    defaultLocale: 'en-NZ',
    currency: 'NZD',
    // Default location: Auckland (largest city)
    defaultLocation: {
      lat: -36.8509,
      lng: 174.7645,
      name: 'Auckland',
    },
    distanceUnit: 'km',
    searchRadius: 1000,
  },
  'kl-uk': {
    siteId: 'kl-uk',
    country: 'United Kingdom',
    countryCode: 'GB',
    defaultLocale: 'en-GB',
    currency: 'GBP',
    // Default location: London
    defaultLocation: {
      lat: 51.5074,
      lng: -0.1278,
      name: 'London',
    },
    distanceUnit: 'km',
    searchRadius: 1000,
  },
  'kl-sg': {
    siteId: 'kl-sg',
    country: 'Singapore',
    countryCode: 'SG',
    defaultLocale: 'en-SG',
    currency: 'SGD',
    // Default location: Singapore center
    defaultLocation: {
      lat: 1.3521,
      lng: 103.8198,
      name: 'Singapore',
    },
    distanceUnit: 'km',
    searchRadius: 1000,
  },
  'kl-my': {
    siteId: 'kl-my',
    country: 'Malaysia',
    countryCode: 'MY',
    defaultLocale: 'en-MY',
    currency: 'MYR',
    // Default location: Kuala Lumpur
    defaultLocation: {
      lat: 3.1390,
      lng: 101.6869,
      name: 'Kuala Lumpur',
    },
    distanceUnit: 'km',
    searchRadius: 1000,
  },
  'kl-ca': {
    siteId: 'kl-ca',
    country: 'Canada',
    countryCode: 'CA',
    defaultLocale: 'en-CA',
    currency: 'CAD',
    // Default location: Toronto (largest city)
    defaultLocation: {
      lat: 43.6532,
      lng: -79.3832,
      name: 'Toronto',
    },
    distanceUnit: 'km',
    searchRadius: 1000,
  },
  'kl-us': {
    siteId: 'kl-us',
    country: 'United States',
    countryCode: 'US',
    defaultLocale: 'en-US',
    currency: 'USD',
    // Default location: Los Angeles (major population center)
    defaultLocation: {
      lat: 34.0522,
      lng: -118.2437,
      name: 'Los Angeles',
    },
    distanceUnit: 'km',
    searchRadius: 1000,
  },
};

/**
 * Get site configuration by site ID
 * @param {string} siteId - The site ID (e.g., 'kl-au')
 * @returns {Object} Site configuration or default (AU) if not found
 */
export const getSiteConfig = (siteId) => {
  return SITE_CONFIG[siteId] || SITE_CONFIG['kl-au'];
};

/**
 * Get default location for a site
 * @param {string} siteId - The site ID
 * @returns {Object} Default location { lat, lng, name }
 */
export const getDefaultLocation = (siteId) => {
  const config = getSiteConfig(siteId);

  return config.defaultLocation;
};

/**
 * Get distance unit for a site
 * @param {string} siteId - The site ID
 * @returns {string} Distance unit ('km' or 'mi')
 */
export const getDistanceUnit = (siteId) => {
  const config = getSiteConfig(siteId);

  return config.distanceUnit;
};

export default SITE_CONFIG;
